// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PurchasedProductCard from "@insite/content-library/Components/PurchasedProductCard";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductMiniCard from "@sc/App/Components/Product/ProductMiniCard";
import XngCarousel from "@sc/App/Components/XngCarousel";
import ProductCarouselProduct from "@sc/Overrides/Components/ProductCarouselProduct";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // XNG-CHANGES
    maxNumberOfColumns = "maxNumberOfColumns",
    showNavigation = "showNavigation",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    gridView = "gridView",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        // XNG-CHANGES
        [fields.maxNumberOfColumns]: number;
        [fields.showNavigation]: boolean;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.gridView]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({ id, fields, products, canViewPurchasedProducts, loadPurchasedProducts }: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType: fields.purchaseType });
        }
    }, [fields.purchaseType]);

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }
    // XNG-CHANGES : Adding ProductMiniCard and ProductCarouselProduct components
    return (
        <>
            <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                <Typography {...styles.titleText}>{fields.title}</Typography>
            </TitleWrapper>
            <XngCarousel
                scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                title={fields.title}
                navigation={fields.showNavigation}
                breakpoints={{
                    480: {
                        slidesPerView: 1,
                    },
                    768: {
                        slidesPerView: 2,
                    },
                    1024: {
                        slidesPerView: fields.maxNumberOfColumns,
                        spaceBetween: 32,
                    },
                }}
            >
                {products.map(product =>
                    fields.gridView ? (
                        <ProductCarouselProduct
                            key={product.id}
                            carouselId={id}
                            product={product}
                            showImage={fields.showImage}
                            showBrand={fields.showBrand}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                        />
                    ) : (
                        <ProductMiniCard
                            key={product!.id}
                            product={product!}
                            widgetId={`${id}`}
                            showBrand={fields.showBrand}
                            showImage={fields.showImage}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                        />
                    ),
                )}
            </XngCarousel>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "center", displayName: "Center" },
                    { value: "left", displayName: "Left" },
                    { value: "right", displayName: "Right" },
                ],
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.gridView,
                displayName: "Grid View",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 6,
                defaultValue: 4,
                fieldType: "Contextual",
                sortOrder: 4,
            },
            {
                name: fields.showNavigation,
                displayName: "Navigation Arrows",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 5,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 6,
            },
            {
                name: fields.showTitle,
                displayName: "Product Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 7,
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 13,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 14,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 15,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 16,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 17,
            },
        ],
    },
};

export default widgetModule;
